/**
 * About WriteFlow Section Component
 * Displays information about the WriteFlow platform with expandable content.
 * Expands to the right for a better reading experience.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Feather, X, ChevronRight } from 'lucide-react';

const AboutWriteFlowSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  // Prevent body scroll when expanded
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  return (
    <>
      <div className="sidebar-about">
        <div className="about-header">
          <Feather size={14} className="about-icon" />
          <h3 className="about-title">About</h3>
        </div>
        
        <button
          className="about-expand-trigger"
          onClick={() => setIsExpanded(true)}
        >
          <span>One prompt a day to get your writing flowing. Practice descriptive writing and keep your streak alive.</span>
          <ChevronRight size={16} className="expand-arrow" />
        </button>

        <p style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
          <Link to="/terms" style={{ color: 'inherit' }}>Terms</Link>
          {' · '}
          <Link to="/privacy" style={{ color: 'inherit' }}>Privacy</Link>
        </p>
      </div>

      {/* Expanded overlay */}
      <div className={`about-overlay ${isExpanded ? 'visible' : ''}`} onClick={() => setIsExpanded(false)} />
      
      <div className={`about-expanded-panel ${isExpanded ? 'open' : ''}`}>
        <div className="expanded-header">
          <Feather size={20} />
          <h2>About WriteFlow</h2>
        </div>
        
        <div className="expanded-content">
          <p className="intro-text">
            Writing description is challenging, but the masters make it look effortless. 
            This platform helps you practice and improve your descriptive writing skills through daily prompts.
          </p>
          
          <div className="author-section">
            <h3>Joe Abercrombie</h3>
            <p>Infuses environment with emotional experience. In <em>The First Law</em> trilogy, he describes a character waiting impatiently:</p>
            <blockquote>
              "'Well?' he snapped. The marble bust at the foot of the stairs offered only its silent contempt. [...] Somewhere in the shadows a clock vomited up a few sluggish chimes."
            </blockquote>
            <p>Masterfully, Abercrombie names objects in the environment and links them to the internal psychology of the character.</p>
          </div>

          <div className="author-section">
            <h3>Dorothy Dunnett</h3>
            <p>Paints with poetic precision, almost flowery but undeniably powerful:</p>
            <blockquote>
              "On the main parapet the sun slapped at the face of turrets and battlements, and far below, the castle rose from the bog like a lighthouse on its circling strands of barmkin, park and moat."
            </blockquote>
            <p>And this one is pure poetry:</p>
            <blockquote>
              "In the long grass by the water's edge a man lay half buried, with small life moving past his head and a tarnishing damp spread into his clothing. [...] The sun moved."
            </blockquote>
            <p>That short last sentence cements the image in your mind.</p>
          </div>

          <p className="closing-text">
            Instead of waiting to become good at descriptions, practice daily. 
            Let this platform substitute scrolling through Pinterest or Tumblr for inspiration.
          </p>
        </div>

        <button className="close-btn" onClick={() => setIsExpanded(false)}>
          <X size={18} />
          <span>Close</span>
        </button>
      </div>
    </>
  );
};

export default AboutWriteFlowSection;
